<template>
  <div class="card podium-card h-100 shadow-sm">
    <div class="card-body d-flex flex-column">
      <div class="podium-title text-uppercase font-weight-bold">
        <i class="fas fa-trophy mr-1"></i>{{ title }}
      </div>
      <div v-if="subTitle" class="podium-subtitle text-muted small mb-2">{{ subTitle }}</div>

      <div v-if="loading" class="d-flex justify-content-center align-items-center flex-grow-1 py-3">
        <div class="spinner-border spinner-border-sm text-muted" role="status">
          <span class="sr-only">Cargando...</span>
        </div>
      </div>

      <div v-else-if="!items || items.length === 0" class="text-muted small text-center py-3 flex-grow-1 d-flex align-items-center justify-content-center">
        Sin datos en el rango seleccionado.
      </div>

      <ul v-else class="podium-list flex-grow-1">
        <li
          v-for="row in rows"
          :key="row.rank"
          v-b-tooltip.hover.left="tooltipText(row)"
          class="podium-row"
          :class="[`rank-${row.rank}`, { 'is-empty': !row.item }]"
        >
          <span class="podium-medal">{{ row.rank }}</span>
          <span class="podium-label">{{ row.item ? row.item.label : '—' }}</span>
          <span class="podium-count">
            {{ row.item ? row.item.count : 0 }}
            <small v-if="row.percent != null" class="podium-percent">{{ formatPercent(row.percent) }}</small>
          </span>
          <span class="podium-bar-track">
            <span class="podium-bar-fill" :style="{ width: row.barWidth + '%' }"></span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WidgetTop5',
  props: {
    title: { type: String, required: true },
    subTitle: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    groupTotal: { type: Number, default: null },
    variant: { type: String, default: 'primary' },
    loading: { type: Boolean, default: false },
  },
  computed: {
    maxCount() {
      return this.items.reduce((m, it) => Math.max(m, it.count || 0), 0);
    },
    rows() {
      const ranks = [1, 2, 3, 4, 5];
      return ranks.map(rank => {
        const item = this.items[rank - 1] || null;
        const count = item ? item.count : 0;
        const barWidth = this.maxCount > 0 ? Math.max(item ? 2 : 0, Math.round((count / this.maxCount) * 100)) : 0;
        const percent = (item && this.groupTotal && this.groupTotal > 0)
          ? Math.round((count / this.groupTotal) * 1000) / 10
          : null;
        return { rank, item, barWidth, percent };
      });
    },
  },
  methods: {
    formatPercent(pct) {
      if (pct == null) return '';
      const decimals = pct % 1 === 0 ? 0 : 1;
      return pct.toFixed(decimals) + '%';
    },
    tooltipText(row) {
      if (!row.item) {
        return `#${row.rank} · Sin datos`;
      }
      const pct = row.percent != null ? ` (${this.formatPercent(row.percent)})` : '';
      return `#${row.rank} · ${row.item.label} — ${row.item.count}${pct}`;
    },
  },
};
</script>

<style scoped>
.podium-card {
  border: 1px solid #e3e6f0;
  background: #ffffff;
}

.podium-title {
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  color: #5a5c69;
}

.podium-title i {
  color: #f6c23e;
}

.podium-subtitle {
  font-size: 0.7rem;
}

.podium-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.podium-row {
  display: grid;
  grid-template-columns: 26px 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 8px;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f1f1f4;
  cursor: help;
}

.podium-row:last-child {
  border-bottom: none;
}

.podium-medal {
  grid-row: 1 / 3;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.rank-1 .podium-medal { background: radial-gradient(circle at 30% 30%, #ffe27a, #f6c23e 60%, #c9941a); }
.rank-2 .podium-medal { background: radial-gradient(circle at 30% 30%, #f1f1f5, #c5c7d0 60%, #8a8d99); }
.rank-3 .podium-medal { background: radial-gradient(circle at 30% 30%, #f0b78a, #cd7f32 60%, #8b561e); }
.rank-4 .podium-medal { background: radial-gradient(circle at 30% 30%, #8aa9f7, #4e73df 60%, #2e4caa); }
.rank-5 .podium-medal { background: radial-gradient(circle at 30% 30%, #d5c7f0, #a98fdc 60%, #7a5db8); }

.podium-label {
  grid-column: 2;
  grid-row: 1;
  font-size: 0.78rem;
  color: #3a3b45;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.podium-count {
  grid-column: 3;
  grid-row: 1;
  font-size: 0.85rem;
  font-weight: 700;
  color: #3a3b45;
  font-variant-numeric: tabular-nums;
  padding-left: 6px;
  text-align: right;
  white-space: nowrap;
}

.podium-percent {
  display: block;
  font-size: 0.65rem;
  font-weight: 500;
  color: #858796;
  line-height: 1;
  margin-top: 1px;
}

.podium-bar-track {
  grid-column: 2 / 4;
  grid-row: 2;
  height: 4px;
  background: #f1f1f4;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 3px;
}

.podium-bar-fill {
  display: block;
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.rank-1 .podium-bar-fill { background: #f6c23e; }
.rank-2 .podium-bar-fill { background: #c5c7d0; }
.rank-3 .podium-bar-fill { background: #cd7f32; }
.rank-4 .podium-bar-fill { background: #4e73df; }
.rank-5 .podium-bar-fill { background: #a98fdc; }

.podium-row.is-empty .podium-medal {
  background: #d5d7de;
  color: #8e8f96;
  box-shadow: none;
}
.podium-row.is-empty .podium-label,
.podium-row.is-empty .podium-count {
  color: #b7b9c5;
}
.podium-row.is-empty .podium-bar-fill {
  background: transparent;
}
</style>
